import React, { useRef, useEffect, useCallback } from 'react';
import './HomieMarquee.css';

const HomieMarquee: React.FC = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const singleItemRef = useRef<HTMLDivElement>(null);

  const positionRef = useRef(0);
  const itemWidthRef = useRef(0);
  const animationRef = useRef<number>(0);
  const lastTimeRef = useRef(0);

  const SPEED = 50; // pixels per second - constant regardless of width

  const updateItemWidth = useCallback(() => {
    if (singleItemRef.current) {
      itemWidthRef.current = singleItemRef.current.offsetWidth;
    }
  }, []);

  useEffect(() => {
    // Wait for fonts to load before measuring
    document.fonts.ready.then(() => {
      updateItemWidth();
    });

    // Also update on resize
    window.addEventListener('resize', updateItemWidth);

    lastTimeRef.current = performance.now();

    const animate = () => {
      const now = performance.now();
      const delta = (now - lastTimeRef.current) / 1000;
      lastTimeRef.current = now;

      // Move by fixed pixels per second
      positionRef.current += SPEED * delta;

      // Reset when we've scrolled one item width (seamless because content repeats)
      const itemWidth = itemWidthRef.current;
      if (itemWidth > 0 && positionRef.current >= itemWidth) {
        positionRef.current = positionRef.current % itemWidth;
      }

      if (trackRef.current) {
        trackRef.current.style.transform = `translateX(-${positionRef.current}px)`;
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', updateItemWidth);
    };
  }, [updateItemWidth]);

  const message = "FIND YOUR SWEETSPOT";

  // Single item = text + dot (we measure this for the reset point)
  const singleItem = (
    <>
      <span className="homie-marquee__text">{message}</span>
      <span className="homie-marquee__dot">●</span>
    </>
  );

  // Enough repetitions to always fill viewport + buffer for smooth scrolling
  const repetitions = Array(20).fill(null).map((_, i) => (
    <React.Fragment key={i}>
      <span className="homie-marquee__text">{message}</span>
      <span className="homie-marquee__dot">●</span>
    </React.Fragment>
  ));

  return (
    <div className="homie-marquee">
      {/* Hidden measurement div for single item width */}
      <div ref={singleItemRef} className="homie-marquee__measure" aria-hidden="true">
        {singleItem}
      </div>
      <div className="homie-marquee__track" ref={trackRef}>
        {repetitions}
      </div>
    </div>
  );
};

export default HomieMarquee;
